// pages/dashboard/Meetings.jsx
// Shows all Calendly bookings
// Linked to user reports where possible
// Steve can add notes per meeting

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

// The Calendly webhook URL shown to Steve must always point at the host he
// is actually using. It used to be a hardcoded staging domain, which meant
// pasting it into Calendly sent production bookings to the wrong server.
// Derive it instead: prefer the configured API base when it is absolute,
// otherwise fall back to the origin the dashboard is being served from.
const getWebhookUrl = () => {
  const apiBase = process.env.REACT_APP_API_URL;
  if (apiBase && /^https?:\/\//i.test(apiBase)) {
    return `${apiBase.replace(/\/+$/, '')}/webhooks/calendly`;
  }
  return `${window.location.origin}/api/webhooks/calendly`;
};

const Meetings = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [editingNotes, setEditingNotes] = useState(null);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    fetchMeetings();
  }, [filter]);

  const fetchMeetings = async () => {
    setLoading(true);
    try {
      const url = filter === 'all'
        ? '/dashboard/meetings?limit=50'
        : `/dashboard/meetings?status=${filter}&limit=50`;
      const response = await axios.get(url);
      if (response.data.success) {
        setMeetings(response.data.data.meetings);
      }
    } catch (err) {
      console.error('Meetings fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async (meetingId) => {
    try {
      await axios.put(`/dashboard/meetings/${meetingId}`, { steveNotes: noteText });
      setMeetings(prev => prev.map(m => m._id === meetingId ? { ...m, steveNotes: noteText } : m));
      setEditingNotes(null);
    } catch (err) {
      console.error('Save notes error:', err);
    }
  };

  const handleMarkComplete = async (meetingId) => {
    try {
      await axios.put(`/dashboard/meetings/${meetingId}`, { status: 'completed' });
      setMeetings(prev => prev.map(m => m._id === meetingId ? { ...m, status: 'completed' } : m));
    } catch (err) {
      console.error('Mark complete error:', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const getStatusBadge = (status) => {
    const map = {
      upcoming: { bg: '#EBF5FB', color: '#1A5276', label: 'Upcoming' },
      completed: { bg: '#E8F8EF', color: '#1E8449', label: 'Completed' },
      cancelled: { bg: '#FADBD8', color: '#922B21', label: 'Cancelled' },
      rescheduled: { bg: '#FEF9E7', color: '#A04000', label: 'Rescheduled' },
    };
    const b = map[status] || { bg: '#F4F6F9', color: '#7F8C8D', label: status };
    return <span style={{ backgroundColor: b.bg, color: b.color, padding: '3px 10px', borderRadius: '100px', fontSize: '12px', fontWeight: '500' }}>{b.label}</span>;
  };

  const navItems = [
    { label: 'Dashboard', path: '/admin/dashboard', icon: '📊' },
    { label: 'Submissions', path: '/admin/submissions', icon: '📋' },
    { label: 'Transactions', path: '/admin/transactions', icon: '💳' },
    { label: 'Meetings', path: '/admin/meetings', icon: '📅', active: true },
    { label: 'Enterprise', path: '/admin/enterprise', icon: '🏢' },
    { label: 'Form Maker', path: '/admin/forms', icon: '📝' },
    { label: 'Analytics', path: '/admin/analytics', icon: '📈' },
    { label: 'Settings', path: '/admin/settings', icon: '⚙️' },
  ];

  return (
    <div style={s.page}>
      <div style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
        <nav style={s.nav}>
          {navItems.map(item => (
            <button key={item.path} style={{ ...s.navItem, ...(item.active ? s.navActive : {}) }} onClick={() => navigate(item.path)}>
              <span>{item.icon}</span><span>{item.label}</span>
            </button>
          ))}
        </nav>
        <div style={s.sidebarFooter}>
          <div style={s.adminName}>{admin?.name}</div>
          <button style={s.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Meetings</h1>
            <p style={s.subtitle}>Calendly consultations booked by clients</p>
          </div>
          <a
            href={process.env.REACT_APP_CALENDLY_LINK || 'https://calendly.com'}
            target="_blank"
            rel="noreferrer"
            style={s.btnCalendly}
          >
            Open Calendly →
          </a>
        </div>

        {/* Webhooks notice */}
        <div style={s.noticeBox}>
          <span style={s.noticeIcon}>ℹ️</span>
          <span style={s.noticeText}>
            Meetings appear automatically when clients book via your Calendly link.
            To activate: add your Calendly webhook URL in Calendly → Integrations → Webhooks.
            Webhook URL: <strong>{getWebhookUrl()}</strong>
          </span>
        </div>

        {/* Filters */}
        <div style={s.filters}>
          {['upcoming', 'completed', 'cancelled', 'all'].map(f => (
            <button
              key={f}
              style={{ ...s.filterBtn, ...(filter === f ? s.filterActive : {}) }}
              onClick={() => setFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div style={s.meetingsList}>
          {loading ? (
            <p style={s.loadingText}>Loading meetings...</p>
          ) : meetings.length === 0 ? (
            <div style={s.empty}>
              <div style={s.emptyIcon}>📅</div>
              <p style={s.emptyTitle}>No meetings yet</p>
              <p style={s.emptyText}>
                Meetings will appear here automatically when clients book via your Calendly link.
                Make sure the Calendly webhook is configured.
              </p>
            </div>
          ) : (
            meetings.map(meeting => (
              <div key={meeting._id} style={s.meetingCard}>
                <div style={s.meetingHeader}>
                  <div style={s.meetingInfo}>
                    <div style={s.meetingName}>{meeting.attendeeName || 'Unknown'}</div>
                    <div style={s.meetingEmail}>{meeting.attendeeEmail}</div>
                    {meeting.userId && (
                      <button
                        style={s.btnViewReport}
                        onClick={() => navigate(`/admin/submissions/${meeting.reportId}`)}
                      >
                        View Their Report →
                      </button>
                    )}
                  </div>
                  <div style={s.meetingMeta}>
                    {getStatusBadge(meeting.status)}
                    {meeting.scheduledAt && (
                      <div style={s.meetingTime}>
                        📅 {new Date(meeting.scheduledAt).toLocaleDateString('en-US', {
                          weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
                        })}
                        <br />
                        🕐 {new Date(meeting.scheduledAt).toLocaleTimeString('en-US', {
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    )}
                    {meeting.joinUrl && meeting.status === 'upcoming' && (
                      <a href={meeting.joinUrl} target="_blank" rel="noreferrer" style={s.btnJoin}>
                        Join Meeting
                      </a>
                    )}
                  </div>
                </div>

                {/* Notes section */}
                <div style={s.notesSection}>
                  {editingNotes === meeting._id ? (
                    <div>
                      <textarea
                        style={s.notesTextarea}
                        value={noteText}
                        onChange={e => setNoteText(e.target.value)}
                        placeholder="Add your meeting notes here..."
                        rows={3}
                      />
                      <div style={s.notesActions}>
                        <button style={s.btnSaveNotes} onClick={() => handleSaveNotes(meeting._id)}>Save Notes</button>
                        <button style={s.btnCancelNotes} onClick={() => setEditingNotes(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={s.notesView}>
                      <span style={s.notesText}>
                        {meeting.steveNotes || 'No notes yet'}
                      </span>
                      <div style={s.notesBtns}>
                        <button
                          style={s.btnEditNotes}
                          onClick={() => { setEditingNotes(meeting._id); setNoteText(meeting.steveNotes || ''); }}
                        >
                          {meeting.steveNotes ? 'Edit Notes' : 'Add Notes'}
                        </button>
                        {meeting.status === 'upcoming' && (
                          <button style={s.btnComplete} onClick={() => handleMarkComplete(meeting._id)}>
                            Mark Complete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: 'var(--font-primary)' },
  sidebar: { width: '240px', backgroundColor: 'var(--color-primary)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  sidebarLogo: { display: 'flex', flexDirection: 'column', gap: '2px', padding: '24px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#fff', lineHeight: 1 },
  nav: { flex: 1, padding: '16px 0' },
  navItem: { width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 20px', background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '14px', cursor: 'pointer', textAlign: 'left' },
  navActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
  sidebarFooter: { padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.1)' },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff', marginBottom: '10px' },
  logoutBtn: { width: '100%', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', color: 'rgba(255,255,255,0.5)', fontSize: '12px', cursor: 'pointer' },
  main: { flex: 1, backgroundColor: 'var(--color-dashboard-bg)', padding: '32px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  subtitle: { fontSize: '14px', color: 'var(--color-text-light)' },
  btnCalendly: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', textDecoration: 'none' },
  noticeBox: { display: 'flex', gap: '10px', alignItems: 'flex-start', backgroundColor: '#EBF5FB', border: '1px solid #AED6F1', borderRadius: '10px', padding: '14px 16px', marginBottom: '20px' },
  noticeIcon: { fontSize: '16px', flexShrink: 0 },
  noticeText: { fontSize: '13px', color: '#1A5276', lineHeight: '1.6' },
  filters: { display: 'flex', gap: '8px', marginBottom: '20px' },
  filterBtn: { padding: '8px 16px', borderRadius: '100px', border: '1.5px solid var(--color-border)', backgroundColor: '#fff', color: 'var(--color-text-light)', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  filterActive: { border: '1.5px solid var(--color-teal)', backgroundColor: 'var(--color-info-bg)', color: 'var(--color-teal)' },
  meetingsList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  loadingText: { padding: '40px', textAlign: 'center', color: 'var(--color-text-light)' },
  empty: { padding: '60px 20px', textAlign: 'center', backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: 'var(--color-primary)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: 'var(--color-text-light)', maxWidth: '400px', margin: '0 auto', lineHeight: '1.6' },
  meetingCard: { backgroundColor: '#fff', borderRadius: '12px', border: '1px solid var(--color-border)', padding: '20px', boxShadow: 'var(--shadow-sm)' },
  meetingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  meetingInfo: {},
  meetingName: { fontSize: '16px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  meetingEmail: { fontSize: '13px', color: 'var(--color-text-light)', marginBottom: '8px' },
  btnViewReport: { background: 'none', border: 'none', color: 'var(--color-teal)', fontSize: '13px', cursor: 'pointer', fontWeight: '500', padding: 0 },
  meetingMeta: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' },
  meetingTime: { fontSize: '13px', color: 'var(--color-text-light)', textAlign: 'right', lineHeight: '1.6' },
  btnJoin: { backgroundColor: '#27AE60', color: '#fff', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '500', textDecoration: 'none' },
  notesSection: { borderTop: '1px solid var(--color-border)', paddingTop: '14px' },
  notesView: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  notesText: { fontSize: '13px', color: 'var(--color-text-light)', fontStyle: 'italic', flex: 1 },
  notesBtns: { display: 'flex', gap: '8px' },
  btnEditNotes: { background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text)', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  btnComplete: { background: 'none', border: '1px solid #27AE60', color: '#27AE60', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  notesTextarea: { width: '100%', padding: '10px', border: '1.5px solid var(--color-border)', borderRadius: '8px', fontSize: '13px', resize: 'vertical', outline: 'none', boxSizing: 'border-box' },
  notesActions: { display: 'flex', gap: '8px', marginTop: '8px' },
  btnSaveNotes: { backgroundColor: 'var(--color-teal)', color: '#fff', padding: '8px 16px', borderRadius: '6px', border: 'none', fontSize: '13px', cursor: 'pointer' },
  btnCancelNotes: { backgroundColor: 'var(--color-off-white)', color: 'var(--color-text)', padding: '8px 16px', borderRadius: '6px', border: '1px solid var(--color-border)', fontSize: '13px', cursor: 'pointer' },
};

export default Meetings;
